'use client';

/**
 * Board Presentation Generator
 * Generate executive-ready presentations from metrics and data
 */

import { useState, useEffect, useCallback } from 'react';
import PptxGenJS from 'pptxgenjs';
import ToolSEO from '@/components/ToolSEO';
import { SlideEditor } from '@/components/board-presentation/SlideEditor';
import {
  BoardPresentation,
  PresentationType,
  PresentationSlide,
  SlideType,
  PRESENTATION_TYPE_CONFIG,
  SLIDE_TYPE_CONFIG,
} from '@/lib/board-presentation/types';
import {
  savePresentationToStorage,
  loadPresentationFromStorage,
  listPresentationsFromStorage,
  deletePresentationFromStorage,
  generatePresentationId,
  generateSlideId,
} from '@/lib/board-presentation/storage';

// Tier access levels
const TIER_HIERARCHY = { free: 0, pro: 1, team: 2 };
type UserTier = 'free' | 'pro' | 'team';

function getFeatureAccess(tier: UserTier) {
  const tierLevel = TIER_HIERARCHY[tier];
  return {
    maxPresentations: tier === 'free' ? 2 : tier === 'pro' ? 10 : Infinity,
    maxSlides: tier === 'free' ? 5 : tier === 'pro' ? 20 : Infinity,
    canExportPDF: tierLevel >= TIER_HIERARCHY.free,
    canExportPPTX: tierLevel >= TIER_HIERARCHY.pro,
    canUseTemplates: tierLevel >= TIER_HIERARCHY.free,
  };
}

function createDefaultPresentation(type: PresentationType): BoardPresentation {
  const config = PRESENTATION_TYPE_CONFIG[type];
  const now = new Date().toISOString();

  const slides: PresentationSlide[] = config.defaultSlides.map((slideType, index) => ({
    id: generateSlideId(),
    slide_type: slideType,
    title: SLIDE_TYPE_CONFIG[slideType].label,
    content: getDefaultContent(slideType),
    order: index,
  }));

  return {
    id: generatePresentationId(),
    name: `New ${config.label}`,
    presentation_type: type,
    slides,
    settings: {},
    created_at: now,
    updated_at: now,
  };
}

function getDefaultContent(slideType: SlideType): PresentationSlide['content'] {
  switch (slideType) {
    case 'title':
      return {
        title: '',
        subtitle: '',
        date: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      };
    case 'executive_summary':
      return { highlights: [''], concerns: [''] };
    case 'kpis':
      return { kpis: [] };
    case 'risks_opportunities':
      return { items: [] };
    case 'investment_request':
      return { investments: [], totalBudget: 0 };
    case 'roadmap':
      return { roadmapItems: [] };
    default:
      return {};
  }
}

export default function BoardPresentationPage() {
  // State
  const [userTier] = useState<UserTier>('pro');
  const [presentations, setPresentations] = useState<BoardPresentation[]>([]);
  const [currentPresentation, setCurrentPresentation] = useState<BoardPresentation | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showAddSlideDialog, setShowAddSlideDialog] = useState(false);
  const [selectedType, setSelectedType] = useState<PresentationType>('monthly_update');

  const features = getFeatureAccess(userTier);

  // Load presentations list
  const loadPresentations = useCallback(async () => {
    try {
      setLoading(true);
      const list = await listPresentationsFromStorage();
      setPresentations(list);
    } catch (err) {
      console.error('[BoardPresentation] Failed to load:', err);
      setError('Failed to load presentations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPresentations();
  }, [loadPresentations]);

  // Auto-save
  useEffect(() => {
    if (!currentPresentation || !hasChanges) return;

    const timer = setTimeout(async () => {
      try {
        setSaving(true);
        const updated = {
          ...currentPresentation,
          updated_at: new Date().toISOString(),
        };
        await savePresentationToStorage(updated);
        setCurrentPresentation(updated);
        setHasChanges(false);
        // Update list
        setPresentations((prev) =>
          prev.map((p) => (p.id === updated.id ? updated : p))
        );
      } catch (err) {
        console.error('[BoardPresentation] Save failed:', err);
      } finally {
        setSaving(false);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [currentPresentation, hasChanges]);

  // Handlers
  const handleCreatePresentation = async () => {
    if (presentations.length >= features.maxPresentations) {
      setError(`Free tier limited to ${features.maxPresentations} presentations. Upgrade to Pro for more.`);
      return;
    }

    const newPresentation = createDefaultPresentation(selectedType);
    await savePresentationToStorage(newPresentation);
    setPresentations((prev) => [newPresentation, ...prev]);
    setCurrentPresentation(newPresentation);
    setShowNewDialog(false);
  };

  const handleOpenPresentation = async (id: string) => {
    try {
      const pres = await loadPresentationFromStorage(id);
      if (pres) {
        setCurrentPresentation(pres);
        setHasChanges(false);
      }
    } catch (err) {
      console.error('[BoardPresentation] Failed to open:', err);
      setError('Failed to open presentation');
    }
  };

  const handleDeletePresentation = async (id: string) => {
    if (!confirm('Delete this presentation?')) return;
    await deletePresentationFromStorage(id);
    setPresentations((prev) => prev.filter((p) => p.id !== id));
    if (currentPresentation?.id === id) {
      setCurrentPresentation(null);
    }
  };

  const handleUpdateSlide = (slideId: string, updated: PresentationSlide) => {
    if (!currentPresentation) return;
    setCurrentPresentation({
      ...currentPresentation,
      slides: currentPresentation.slides.map((s) => (s.id === slideId ? updated : s)),
    });
    setHasChanges(true);
  };

  const handleDeleteSlide = (slideId: string) => {
    if (!currentPresentation) return;
    const newSlides = currentPresentation.slides
      .filter((s) => s.id !== slideId)
      .map((s, idx) => ({ ...s, order: idx }));
    setCurrentPresentation({ ...currentPresentation, slides: newSlides });
    setHasChanges(true);
  };

  const handleMoveSlide = (slideId: string, direction: 'up' | 'down') => {
    if (!currentPresentation) return;
    const slides = [...currentPresentation.slides];
    const idx = slides.findIndex((s) => s.id === slideId);
    if (idx < 0) return;

    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= slides.length) return;

    [slides[idx], slides[newIdx]] = [slides[newIdx], slides[idx]];
    const reordered = slides.map((s, i) => ({ ...s, order: i }));
    setCurrentPresentation({ ...currentPresentation, slides: reordered });
    setHasChanges(true);
  };

  const handleAddSlide = (slideType: SlideType) => {
    if (!currentPresentation) return;
    if (currentPresentation.slides.length >= features.maxSlides) {
      setError(`Free tier limited to ${features.maxSlides} slides per presentation.`);
      return;
    }

    const newSlide: PresentationSlide = {
      id: generateSlideId(),
      slide_type: slideType,
      title: SLIDE_TYPE_CONFIG[slideType].label,
      content: getDefaultContent(slideType),
      order: currentPresentation.slides.length,
    };

    setCurrentPresentation({
      ...currentPresentation,
      slides: [...currentPresentation.slides, newSlide],
    });
    setHasChanges(true);
    setShowAddSlideDialog(false);
  };

  const handleExportMarkdown = () => {
    if (!currentPresentation) return;

    let md = `# ${currentPresentation.name}\n\n`;
    md += `*${PRESENTATION_TYPE_CONFIG[currentPresentation.presentation_type].label}*\n\n`;
    md += `---\n\n`;

    currentPresentation.slides.forEach((slide, idx) => {
      md += `## Slide ${idx + 1}: ${slide.title}\n\n`;

      switch (slide.slide_type) {
        case 'title':
          if (slide.content.title) md += `### ${slide.content.title}\n\n`;
          if (slide.content.subtitle) md += `${slide.content.subtitle}\n\n`;
          if (slide.content.date) md += `*${slide.content.date}*\n\n`;
          break;
        case 'executive_summary':
          if (slide.content.highlights?.length) {
            md += `**Highlights:**\n`;
            slide.content.highlights.filter(Boolean).forEach((h) => {
              md += `- ${h}\n`;
            });
            md += `\n`;
          }
          if (slide.content.concerns?.length) {
            md += `**Concerns:**\n`;
            slide.content.concerns.filter(Boolean).forEach((c) => {
              md += `- ${c}\n`;
            });
            md += `\n`;
          }
          break;
        case 'kpis':
          if (slide.content.kpis?.length) {
            md += `| Metric | Value | Trend | Target |\n`;
            md += `|--------|-------|-------|--------|\n`;
            slide.content.kpis.forEach((kpi) => {
              const trendIcon = kpi.trend === 'up' ? '↑' : kpi.trend === 'down' ? '↓' : '→';
              md += `| ${kpi.name} | ${kpi.value} | ${trendIcon} | ${kpi.target || '-'} |\n`;
            });
            md += `\n`;
          }
          break;
        case 'risks_opportunities':
          if (slide.content.items?.length) {
            slide.content.items.forEach((item) => {
              const icon = item.type === 'risk' ? '⚠️' : '✨';
              md += `${icon} **${item.type.toUpperCase()}:** ${item.title}\n`;
              md += `- Impact: ${item.impact} | Likelihood: ${item.likelihood}\n`;
              if (item.description) md += `- ${item.description}\n`;
              if (item.mitigation) md += `- Mitigation: ${item.mitigation}\n`;
              md += `\n`;
            });
          }
          break;
        case 'investment_request':
          if (slide.content.investments?.length) {
            const total = slide.content.investments.reduce((sum, i) => sum + (i.amount || 0), 0);
            md += `**Total Investment: $${total.toLocaleString()}**\n\n`;
            slide.content.investments.forEach((inv) => {
              md += `- **${inv.title}**: $${inv.amount.toLocaleString()} (${inv.category})\n`;
              if (inv.justification) md += `  - ${inv.justification}\n`;
            });
            md += `\n`;
          }
          break;
        case 'roadmap':
          if (slide.content.roadmapItems?.length) {
            md += `| Initiative | Quarter | Status | Team |\n`;
            md += `|------------|---------|--------|------|\n`;
            slide.content.roadmapItems.forEach((item) => {
              md += `| ${item.title} | ${item.quarter} | ${item.status} | ${item.team || '-'} |\n`;
            });
            md += `\n`;
          }
          break;
        case 'custom':
          if (slide.content.customMarkdown) {
            md += slide.content.customMarkdown + `\n\n`;
          }
          break;
      }

      if (slide.notes) {
        md += `> *Speaker notes: ${slide.notes}*\n\n`;
      }

      md += `---\n\n`;
    });

    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentPresentation.name.replace(/\s+/g, '-').toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPowerPoint = async () => {
    if (!currentPresentation) return;
    if (!features.canExportPPTX) {
      setError('PowerPoint export requires Pro tier. Upgrade to access this feature.');
      return;
    }

    try {
      const pptx = new PptxGenJS();

      // Set presentation properties
      pptx.author = 'The Art of CTO';
      pptx.title = currentPresentation.name;
      pptx.subject = PRESENTATION_TYPE_CONFIG[currentPresentation.presentation_type].label;
      pptx.company = 'The Art of CTO';

      // Define colors matching brand
      const NAVY = '0A1E2E';
      const ORANGE = 'E89841';
      const CREAM = 'E8D5B7';

      // Define master slides
      pptx.defineSlideMaster({
        title: 'MASTER_SLIDE',
        background: { color: NAVY },
        margin: 0.5,
      });

      // Generate slides
      currentPresentation.slides.forEach((slide) => {
        const pptSlide = pptx.addSlide({ masterName: 'MASTER_SLIDE' });

        // Add slide number at bottom
        pptSlide.addText(`${slide.order + 1}`, {
          x: 9.5,
          y: 5.3,
          w: 0.4,
          h: 0.3,
          fontSize: 10,
          color: CREAM,
          bold: false,
        });

        switch (slide.slide_type) {
          case 'title': {
            pptSlide.addText(slide.content.title || currentPresentation.name, {
              x: 0.5, y: 2, w: 9, h: 1.5,
              fontSize: 44, bold: true, color: CREAM, align: 'center',
            });
            if (slide.content.subtitle) {
              pptSlide.addText(slide.content.subtitle, {
                x: 0.5, y: 3.5, w: 9, h: 0.5,
                fontSize: 20, color: ORANGE, align: 'center',
              });
            }
            if (slide.content.date) {
              pptSlide.addText(slide.content.date, {
                x: 0.5, y: 4.5, w: 9, h: 0.3,
                fontSize: 14, color: CREAM, align: 'center',
              });
            }
            break;
          }

          case 'executive_summary': {
            pptSlide.addText(slide.title || 'Executive Summary', {
              x: 0.5, y: 0.3, w: 9, h: 0.6,
              fontSize: 28, bold: true, color: ORANGE,
            });

            const highlights = (slide.content.highlights || []).filter(Boolean);
            const concerns = (slide.content.concerns || []).filter(Boolean);

            if (highlights.length > 0) {
              pptSlide.addText('Highlights', {
                x: 0.5, y: 1.1, w: 4, h: 0.4,
                fontSize: 16, bold: true, color: '22C55E',
              });
              pptSlide.addText(
                highlights.map((h) => ({ text: `• ${h}`, options: { bullet: false, breakLine: true } })),
                { x: 0.5, y: 1.6, w: 4, h: 3.5, fontSize: 14, color: CREAM, valign: 'top' }
              );
            }

            if (concerns.length > 0) {
              pptSlide.addText('Concerns', {
                x: 5, y: 1.1, w: 4.5, h: 0.4,
                fontSize: 16, bold: true, color: 'EF4444',
              });
              pptSlide.addText(
                concerns.map((c) => ({ text: `• ${c}`, options: { bullet: false, breakLine: true } })),
                { x: 5, y: 1.6, w: 4.5, h: 3.5, fontSize: 14, color: CREAM, valign: 'top' }
              );
            }
            break;
          }

          case 'kpis': {
            pptSlide.addText(slide.title || 'Key Performance Indicators', {
              x: 0.5, y: 0.3, w: 9, h: 0.6,
              fontSize: 28, bold: true, color: ORANGE,
            });

            const kpis = slide.content.kpis || [];
            const rows = kpis.map((kpi) => {
              const trendIcon = kpi.trend === 'up' ? '↑' : kpi.trend === 'down' ? '↓' : '→';
              const trendColor = kpi.trend === 'up' ? '22C55E' : kpi.trend === 'down' ? 'EF4444' : CREAM;
              return [
                { text: String(kpi.name || ''), options: { color: CREAM, bold: false } },
                { text: String(kpi.value || ''), options: { color: CREAM, bold: true } },
                { text: trendIcon, options: { color: trendColor, bold: true } },
                { text: String(kpi.target || '-'), options: { color: CREAM, bold: false } },
              ];
            });

            if (rows.length > 0) {
              pptSlide.addTable(
                [
                  [
                    { text: 'Metric', options: { bold: true, color: ORANGE } },
                    { text: 'Value', options: { bold: true, color: ORANGE } },
                    { text: 'Trend', options: { bold: true, color: ORANGE } },
                    { text: 'Target', options: { bold: true, color: ORANGE } },
                  ],
                  ...rows,
                ],
                {
                  x: 0.5, y: 1.2, w: 9, h: 4,
                  colW: [3, 2, 1, 3],
                  border: { pt: 1, color: '3A4A5A' },
                  fill: { color: '1A2E3E' },
                  fontFace: 'Arial', fontSize: 12,
                  valign: 'middle', align: 'left',
                }
              );
            }
            break;
          }

          case 'risks_opportunities': {
            pptSlide.addText(slide.title || 'Risks & Opportunities', {
              x: 0.5, y: 0.3, w: 9, h: 0.6,
              fontSize: 28, bold: true, color: ORANGE,
            });

            const items = slide.content.items || [];
            const risks = items.filter((i) => i.type === 'risk');
            const opps = items.filter((i) => i.type === 'opportunity');

            let yPos = 1.1;

            if (risks.length > 0) {
              pptSlide.addText('⚠️ Risks', {
                x: 0.5, y: yPos, w: 9, h: 0.4,
                fontSize: 16, bold: true, color: 'EF4444',
              });
              yPos += 0.5;

              risks.slice(0, 3).forEach((risk) => {
                pptSlide.addText(risk.title, {
                  x: 0.7, y: yPos, w: 8.5, h: 0.3,
                  fontSize: 14, bold: true, color: CREAM,
                });
                yPos += 0.35;
                pptSlide.addText(`Impact: ${risk.impact} | Likelihood: ${risk.likelihood}`, {
                  x: 0.7, y: yPos, w: 8.5, h: 0.25,
                  fontSize: 11, color: 'A0A0A0',
                });
                yPos += 0.35;
              });
            }

            if (opps.length > 0) {
              yPos += 0.2;
              pptSlide.addText('✨ Opportunities', {
                x: 0.5, y: yPos, w: 9, h: 0.4,
                fontSize: 16, bold: true, color: '22C55E',
              });
              yPos += 0.5;

              opps.slice(0, 3).forEach((opp) => {
                pptSlide.addText(opp.title, {
                  x: 0.7, y: yPos, w: 8.5, h: 0.3,
                  fontSize: 14, bold: true, color: CREAM,
                });
                yPos += 0.35;
                pptSlide.addText(`Impact: ${opp.impact} | Likelihood: ${opp.likelihood}`, {
                  x: 0.7, y: yPos, w: 8.5, h: 0.25,
                  fontSize: 11, color: 'A0A0A0',
                });
                yPos += 0.35;
              });
            }
            break;
          }

          case 'investment_request': {
            pptSlide.addText(slide.title || 'Investment Request', {
              x: 0.5, y: 0.3, w: 9, h: 0.6,
              fontSize: 28, bold: true, color: ORANGE,
            });

            const investments = slide.content.investments || [];
            const total = investments.reduce((sum, i) => sum + (i.amount || 0), 0);

            pptSlide.addText(`Total Investment: $${total.toLocaleString()}`, {
              x: 0.5, y: 1.0, w: 9, h: 0.5,
              fontSize: 20, bold: true, color: '22C55E',
            });

            const rows = investments.map((inv) => [
              { text: String(inv.title || ''), options: { color: CREAM, bold: false } },
              { text: String(inv.category || ''), options: { color: CREAM, bold: false } },
              { text: `$${(inv.amount || 0).toLocaleString()}`, options: { color: CREAM, bold: true } },
            ]);

            if (rows.length > 0) {
              pptSlide.addTable(
                [
                  [
                    { text: 'Initiative', options: { bold: true, color: ORANGE } },
                    { text: 'Category', options: { bold: true, color: ORANGE } },
                    { text: 'Amount', options: { bold: true, color: ORANGE } },
                  ],
                  ...rows,
                ],
                {
                  x: 0.5, y: 1.7, w: 9, h: 3.5,
                  colW: [4, 2.5, 2.5],
                  border: { pt: 1, color: '3A4A5A' },
                  fill: { color: '1A2E3E' },
                  fontFace: 'Arial', fontSize: 12,
                  valign: 'middle', align: 'left',
                }
              );
            }
            break;
          }

          case 'roadmap': {
            pptSlide.addText(slide.title || 'Roadmap', {
              x: 0.5, y: 0.3, w: 9, h: 0.6,
              fontSize: 28, bold: true, color: ORANGE,
            });

            const items = slide.content.roadmapItems || [];
            const rows = items.map((item) => {
              const statusColor = item.status === 'completed' ? '22C55E' : item.status === 'in_progress' ? ORANGE : CREAM;
              return [
                { text: String(item.title || ''), options: { color: CREAM, bold: false } },
                { text: String(item.quarter || ''), options: { color: CREAM, bold: false } },
                { text: String(item.status || '').replace('_', ' ').toUpperCase(), options: { color: statusColor, bold: true } },
                { text: String(item.team || '-'), options: { color: CREAM, bold: false } },
              ];
            });

            if (rows.length > 0) {
              pptSlide.addTable(
                [
                  [
                    { text: 'Initiative', options: { bold: true, color: ORANGE } },
                    { text: 'Quarter', options: { bold: true, color: ORANGE } },
                    { text: 'Status', options: { bold: true, color: ORANGE } },
                    { text: 'Team', options: { bold: true, color: ORANGE } },
                  ],
                  ...rows,
                ],
                {
                  x: 0.5, y: 1.2, w: 9, h: 4,
                  colW: [3.5, 1.5, 2, 2],
                  border: { pt: 1, color: '3A4A5A' },
                  fill: { color: '1A2E3E' },
                  fontFace: 'Arial', fontSize: 12,
                  valign: 'middle', align: 'left',
                }
              );
            }
            break;
          }

          case 'custom':
          default: {
            pptSlide.addText(slide.title || 'Custom Slide', {
              x: 0.5, y: 0.3, w: 9, h: 0.6,
              fontSize: 28, bold: true, color: ORANGE,
            });

            if (slide.content.customMarkdown) {
              pptSlide.addText(slide.content.customMarkdown, {
                x: 0.5, y: 1.2, w: 9, h: 4,
                fontSize: 14, color: CREAM, valign: 'top',
              });
            }
            break;
          }
        }

        if (slide.notes) {
          pptSlide.addNotes(slide.notes);
        }
      });

      const fileName = `${currentPresentation.name.replace(/\s+/g, '-').toLowerCase()}.pptx`;
      await pptx.writeFile({ fileName });
    } catch (err) {
      console.error('[BoardPresentation] PowerPoint export failed:', err);
      setError('Failed to export PowerPoint. Please try again.');
    }
  };

  // Presentation list view
  if (!currentPresentation) {
    return (
      <>
        <ToolSEO
          toolName="Board Presentation Generator"
          toolSlug="board-presentation"
          description="Generate executive-ready board presentations with KPIs, risks, investments, and roadmaps."
          category="planner"
          isPremium={true}
        />
        <main className="min-h-screen bg-background text-foreground py-12 px-6">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold">Board Presentation Generator</h1>
                <p className="text-foreground-secondary mt-2">
                  Create executive-ready presentations with metrics, risks, and roadmaps
                </p>
              </div>
              <button
                onClick={() => setShowNewDialog(true)}
                className="px-4 py-2 bg-accent text-accent-foreground rounded font-medium hover:bg-accent-hover transition-colors"
              >
                + New Presentation
              </button>
            </div>

            {/* Tier badge */}
            <div className="mb-6 flex items-center gap-4">
              <span
                className={`px-3 py-1 rounded text-sm font-medium ${
                  userTier === 'team'
                    ? 'bg-purple-500/20 text-purple-400'
                    : userTier === 'pro'
                    ? 'bg-accent-muted text-accent'
                    : 'bg-cream/10 text-foreground-tertiary'
                }`}
              >
                {userTier.toUpperCase()} Plan
              </span>
              <span className="text-sm text-foreground-tertiary">
                {presentations.length}/{features.maxPresentations === Infinity ? '∞' : features.maxPresentations} presentations
              </span>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
                {error}
                <button onClick={() => setError(null)} className="ml-4 underline">
                  Dismiss
                </button>
              </div>
            )}

            {/* Loading */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-foreground-tertiary">Loading presentations...</p>
              </div>
            ) : presentations.length === 0 ? (
              <div className="border border-dashed border-border rounded-lg p-12 text-center">
                <div className="text-4xl mb-4">📊</div>
                <h3 className="text-lg font-medium mb-2">No presentations yet</h3>
                <p className="text-foreground-tertiary mb-6">
                  Create your first board presentation to get started
                </p>
                <button
                  onClick={() => setShowNewDialog(true)}
                  className="px-6 py-3 bg-accent text-accent-foreground rounded font-medium hover:bg-accent-hover transition-colors"
                >
                  Create Presentation
                </button>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {presentations.map((pres) => (
                  <div
                    key={pres.id}
                    className="p-6 border border-border rounded-lg hover:border-accent/40 transition-colors cursor-pointer group"
                    onClick={() => handleOpenPresentation(pres.id)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-foreground group-hover:text-accent transition-colors">
                          {pres.name}
                        </h3>
                        <p className="text-sm text-foreground-tertiary">
                          {PRESENTATION_TYPE_CONFIG[pres.presentation_type].label}
                        </p>
                      </div>
                      <span className="text-sm text-foreground-muted">
                        {pres.slides.length} slides
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-foreground-muted">
                      <span>Updated {new Date(pres.updated_at).toLocaleDateString()}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePresentation(pres.id);
                        }}
                        className="text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* New Presentation Dialog */}
            {showNewDialog && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-background border border-border rounded-lg max-w-md w-full p-6">
                  <h3 className="text-xl font-semibold mb-4">New Presentation</h3>
                  <p className="text-foreground-tertiary text-sm mb-4">
                    Choose a presentation type to get started with pre-configured slides.
                  </p>
                  <div className="space-y-2 mb-6">
                    {(Object.keys(PRESENTATION_TYPE_CONFIG) as PresentationType[]).map((type) => {
                      const config = PRESENTATION_TYPE_CONFIG[type];
                      return (
                        <button
                          key={type}
                          onClick={() => setSelectedType(type)}
                          className={`w-full p-4 text-left rounded-lg border transition-colors ${
                            selectedType === type
                              ? 'border-accent bg-accent-muted'
                              : 'border-border hover:border-border/40'
                          }`}
                        >
                          <div className="font-medium">{config.label}</div>
                          <div className="text-sm text-foreground-tertiary">{config.description}</div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowNewDialog(false)}
                      className="px-4 py-2 text-foreground-secondary hover:text-foreground"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreatePresentation}
                      className="px-4 py-2 bg-accent text-accent-foreground rounded font-medium"
                    >
                      Create
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </>
    );
  }

  // Presentation editor view
  return (
    <>
      <ToolSEO
        toolName="Board Presentation Generator"
        toolSlug="board-presentation"
        description="Generate executive-ready board presentations with KPIs, risks, investments, and roadmaps."
        category="planner"
        isPremium={true}
      />
      <main className="min-h-screen bg-background text-foreground py-8 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setCurrentPresentation(null)}
                className="text-foreground-tertiary hover:text-foreground transition-colors"
              >
                ← Back
              </button>
              <div>
                <input
                  type="text"
                  value={currentPresentation.name}
                  onChange={(e) => {
                    setCurrentPresentation({ ...currentPresentation, name: e.target.value });
                    setHasChanges(true);
                  }}
                  className="text-2xl font-bold bg-transparent border-none text-foreground focus:outline-none"
                />
                <div className="text-sm text-foreground-tertiary">
                  {PRESENTATION_TYPE_CONFIG[currentPresentation.presentation_type].label}
                  {saving && <span className="ml-2 text-accent">Saving...</span>}
                  {!saving && hasChanges && <span className="ml-2 text-foreground-muted">Unsaved changes</span>}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleExportMarkdown}
                className="px-4 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20 transition-colors"
              >
                Export Markdown
              </button>
              {features.canExportPPTX && (
                <button
                  onClick={handleExportPowerPoint}
                  className="px-4 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20 transition-colors"
                >
                  Export PowerPoint
                </button>
              )}
              <button
                onClick={() => setShowAddSlideDialog(true)}
                className="px-4 py-2 bg-accent text-accent-foreground rounded font-medium hover:bg-accent-hover transition-colors"
              >
                + Add Slide
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
              {error}
              <button onClick={() => setError(null)} className="ml-4 underline">
                Dismiss
              </button>
            </div>
          )}

          {/* Slides */}
          <div className="space-y-4">
            {currentPresentation.slides
              .sort((a, b) => a.order - b.order)
              .map((slide, idx) => (
                <SlideEditor
                  key={slide.id}
                  slide={slide}
                  onUpdate={(updated) => handleUpdateSlide(slide.id, updated)}
                  onDelete={() => handleDeleteSlide(slide.id)}
                  onMoveUp={() => handleMoveSlide(slide.id, 'up')}
                  onMoveDown={() => handleMoveSlide(slide.id, 'down')}
                  canMoveUp={idx > 0}
                  canMoveDown={idx < currentPresentation.slides.length - 1}
                />
              ))}
          </div>

          {currentPresentation.slides.length === 0 && (
            <div className="border border-dashed border-border rounded-lg p-12 text-center">
              <div className="text-4xl mb-4">📑</div>
              <h3 className="text-lg font-medium mb-2">No slides yet</h3>
              <p className="text-foreground-tertiary mb-6">Add your first slide to get started</p>
              <button
                onClick={() => setShowAddSlideDialog(true)}
                className="px-6 py-3 bg-accent text-accent-foreground rounded font-medium hover:bg-accent-hover transition-colors"
              >
                Add Slide
              </button>
            </div>
          )}

          {/* Add Slide Dialog */}
          {showAddSlideDialog && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-background border border-border rounded-lg max-w-md w-full p-6">
                <h3 className="text-xl font-semibold mb-4">Add Slide</h3>
                <div className="grid grid-cols-2 gap-2">
                  {(Object.keys(SLIDE_TYPE_CONFIG) as SlideType[]).map((type) => {
                    const config = SLIDE_TYPE_CONFIG[type];
                    return (
                      <button
                        key={type}
                        onClick={() => handleAddSlide(type)}
                        className="p-4 text-left border border-border rounded-lg hover:border-accent/40 transition-colors"
                      >
                        <div className="text-2xl mb-2">{config.icon}</div>
                        <div className="text-sm font-medium">{config.label}</div>
                        <div className="text-xs text-foreground-tertiary mt-1">{config.description}</div>
                      </button>
                    );
                  })}
                </div>
                <div className="flex justify-end mt-4">
                  <button
                    onClick={() => setShowAddSlideDialog(false)}
                    className="px-4 py-2 text-foreground-secondary hover:text-foreground"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}
